import React, { useEffect, useMemo, useState } from 'react';
import { AddOutlined as AddOutlinedIcon, EditOutlined as EditOutlinedIcon, PeopleOutline as PeopleOutlineIcon } from '@mui/icons-material';
import { Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, Stack, TextField, Typography } from '@mui/material';
import { collection, doc, getDocs, onSnapshot, query, updateDoc, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';

const BORDER = '#e8e8e8';
const PANEL = '#f3f0ea';
const GOLD_D = '#7a5525';
const MUTED = '#6b6b6b';
const DARK = '#1a1a1a';

type OfficeUser = {
  id: string;
  name?: string;
  email?: string;
  roleId?: string;
  officePageAccess?: string[];
  officeActionAccess?: string[];
  officeProjectIds?: string[];
};

const PAGE_OPTIONS = [
  {
    key: 'officeMaterialOrders',
    label: 'Material Orders & Receipts',
    actions: [
      { key: 'materialOrders.view', label: 'View orders and receipt history' },
      { key: 'materialOrders.receive', label: 'Add material receipts and challan details' },
      { key: 'materialOrders.manage', label: 'Create, edit, cancel, and merge orders' },
      { key: 'materialOrders.bill', label: 'Add, correct, and view vendor bills' },
      { key: 'materialOrders.billDirect', label: 'Also add bills without an order or delivery, and link them later (needs the bill permission)' },
    ],
  },
  {
    key: 'officePayroll',
    label: 'Payroll',
    actions: [
      { key: 'payroll.view', label: 'View monthly payroll' },
      { key: 'payroll.manage', label: 'Add and edit attendance, salary, and TDS' },
      { key: 'payroll.audit', label: 'View attendance change history' },
      { key: 'payroll.approve', label: 'Approve monthly payroll' },
      { key: 'payroll.pay', label: 'Mark payroll as paid' },
    ],
  },
  {
    key: 'officeSops',
    label: 'Agency SOPs',
    actions: [
      { key: 'sops.project', label: 'Add or turn off SOP points for their projects (everyone with the page can view SOPs and add lessons)' },
      { key: 'sops.manage', label: 'Change the global SOPs, add agencies and review lessons' },
    ],
  },
];

export default function OfficeUserManagementPage({ embedded = false }: { embedded?: boolean } = {}) {
  const [users, setUsers] = useState<OfficeUser[]>([]);
  const [projects, setProjects] = useState<Array<{ id: string; name?: string }>>([]);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<OfficeUser | null>(null);
  const [pages, setPages] = useState<string[]>([]);
  const [actions, setActions] = useState<string[]>([]);
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState('');
  const [addEmail, setAddEmail] = useState('');
  const [addPhone, setAddPhone] = useState('');
  const [addPassword, setAddPassword] = useState('');
  const [addPages, setAddPages] = useState<string[]>([]);
  const [addActions, setAddActions] = useState<string[]>([]);
  const [addProjectIds, setAddProjectIds] = useState<string[]>([]);
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState('');

  useEffect(() => onSnapshot(query(collection(db, 'users')), (snapshot) => {
    setUsers(snapshot.docs.map((item) => ({ id: item.id, ...(item.data() as Omit<OfficeUser, 'id'>) }))); 
  }, () => setError('Could not load Office users.')), []);
  useEffect(() => onSnapshot(query(collection(db, 'projects')), (snapshot) => {
    setProjects(snapshot.docs.map((item) => ({ id: item.id, name: String(item.data()?.name || '') })).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
  }, () => setError('Could not load projects.')), []);

  const visibleUsers = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((user) => `${user.name || ''} ${user.email || ''} ${user.roleId || ''}`.toLowerCase().includes(needle));
  }, [search, users]);

  const openEditor = (user: OfficeUser) => {
    setEditing(user);
    setPages(user.officePageAccess || []);
    setActions(user.officeActionAccess || []);
    setProjectIds(user.officeProjectIds || []);
    setError('');
  };
  const toggle = (value: string, current: string[], setCurrent: (value: string[]) => void) => {
    setCurrent(current.includes(value) ? current.filter((item) => item !== value) : [...current, value]);
  };
  const allowedActionKeysForPages = (selectedPages: string[]) => PAGE_OPTIONS
    .filter((page) => selectedPages.includes(page.key))
    .flatMap((page) => page.actions.map((action) => action.key));
  const resetAddForm = () => {
    setAddName('');
    setAddEmail('');
    setAddPhone('');
    setAddPassword('');
    setAddPages([]);
    setAddActions([]);
    setAddProjectIds([]);
    setAddError('');
  };
  const closeAddDialog = () => {
    if (addSaving) return;
    setAddOpen(false);
    resetAddForm();
  };
  const createOfficeUser = async () => {
    const name = addName.trim();
    const email = addEmail.trim();
    const phone = addPhone.trim();
    const password = addPassword.trim();
    setAddError('');
    setSuccess('');
    setError('');
    if (!name || !email || !phone || !password) {
      setAddError('Name, email, phone, and password are required.');
      return;
    }
    if (!addPages.length) {
      setAddError('Select at least one Office page for this user.');
      return;
    }
    setAddSaving(true);
    try {
      const allowedActionKeys = allowedActionKeysForPages(addPages);
      const officeActions = addActions.filter((action) => allowedActionKeys.includes(action));
      const functions = getFunctions();
      const createCrmUser = httpsCallable(functions, 'createCrmUser');
      const result = await createCrmUser({ name, email, phone, password, role: 'sales-team' });
      const usersRef = collection(db, 'users');
      const snap = await getDocs(query(usersRef, where('email', '==', email)));
      if (snap.empty) throw new Error('User created, but could not find the user profile to attach Office access.');
      await updateDoc(snap.docs[0].ref, {
        roleId: 'sales-team',
        pageAccess: [],
        actionAccess: [],
        projectIds: [],
        officePageAccess: addPages,
        officeActionAccess: officeActions,
        officeProjectIds: addProjectIds,
      });
      setSuccess(String((result.data as any)?.message || 'Office user created successfully.'));
      setAddOpen(false);
      resetAddForm();
    } catch (err: any) {
      setAddError(err?.message || 'Could not create Office user.');
    } finally {
      setAddSaving(false);
    }
  };
  const save = async () => {
    if (!editing) return;
    setSaving(true);
    setError('');
    try {
      const allowedActionKeys = allowedActionKeysForPages(pages);
      await updateDoc(doc(db, 'users', editing.id), {
        officePageAccess: pages,
        officeActionAccess: actions.filter((action) => allowedActionKeys.includes(action)),
        officeProjectIds: projectIds,
      });
      setEditing(null);
    } catch (err: any) {
      setError(err?.message || 'Could not save Office access.');
    } finally {
      setSaving(false);
    }
  };

  return <Box sx={{ maxWidth: embedded ? 'none' : 1180, mx: 'auto', width: '100%', p: embedded ? 0 : { xs: 1.5, md: 3 } }}>
    <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} gap={1.2} sx={{ mb: 2 }}>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: 23, fontWeight: 900, color: DARK }}>Office User Management</Typography>
        <Typography sx={{ color: MUTED, fontSize: 13 }}>Assign Office-only page access, actions, and projects. CRM permissions are unchanged.</Typography>
      </Box>
      <Stack direction="row" gap={0.8} alignItems="center">
        <Chip icon={<PeopleOutlineIcon />} label={`${users.length} users`} sx={{ bgcolor: PANEL, color: GOLD_D, fontWeight: 800 }} />
        <Button variant="contained" startIcon={<AddOutlinedIcon />} onClick={() => { setAddOpen(true); setAddError(''); setSuccess(''); }} sx={{ whiteSpace: 'nowrap' }}>Add User</Button>
      </Stack>
    </Stack>
    {error && <Alert severity="error" sx={{ mb: 1.25 }}>{error}</Alert>}
    {success && <Alert severity="success" sx={{ mb: 1.25 }}>{success}</Alert>}
    <TextField fullWidth value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search users by name, email, or role" size="small" sx={{ mb: 1.25, maxWidth: 480 }} />
    <Stack gap={0.9}>
      {visibleUsers.map((user) => {
        const hasOrders = (user.officePageAccess || []).includes('officeMaterialOrders');
        const hasPayroll = (user.officePageAccess || []).includes('officePayroll');
        const canReceive = (user.officeActionAccess || []).includes('materialOrders.receive');
        const canManagePayroll = (user.officeActionAccess || []).includes('payroll.manage');
        const hasSops = (user.officePageAccess || []).includes('officeSops');
        return <Box key={user.id} sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, bgcolor: '#fff', p: { xs: 1.2, sm: 1.45 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.1} alignItems={{ sm: 'center' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 900, color: DARK }}>{user.name || 'Unnamed user'}</Typography>
              <Typography sx={{ color: MUTED, fontSize: 12 }}>{user.email || 'No email'}{user.roleId ? ` · ${user.roleId}` : ''}</Typography>
            </Box>
            <Stack direction="row" gap={0.6} flexWrap="wrap">
              {hasOrders ? <Chip size="small" label={canReceive ? 'Orders + receipts' : 'Orders view'} sx={{ bgcolor: '#eef7f0', color: '#3c6b4b', fontWeight: 800 }} /> : null}
              {hasPayroll ? <Chip size="small" label={canManagePayroll ? 'Payroll manage' : 'Payroll view'} sx={{ bgcolor: '#eef3ff', color: '#305c9d', fontWeight: 800 }} /> : null}
              {hasSops ? <Chip size="small" label="Agency SOPs" sx={{ bgcolor: '#f6ecdb', color: GOLD_D, fontWeight: 800 }} /> : null}
              {!hasOrders && !hasPayroll && !hasSops ? <Chip size="small" label="No Office access" sx={{ bgcolor: PANEL, color: MUTED }} /> : null}
              {(hasOrders || hasPayroll || hasSops) &&<Chip size="small" label={`${(user.officeProjectIds || []).length || projects.length} projects`} sx={{ bgcolor: '#fffaf0', color: GOLD_D }} />}
            </Stack>
            <IconButton aria-label={`Edit Office access for ${user.name || user.email || 'user'}`} onClick={() => openEditor(user)} sx={{ color: GOLD_D }}><EditOutlinedIcon /></IconButton>
          </Stack>
        </Box>;
      })}
      {!visibleUsers.length && <Box sx={{ p: 3, textAlign: 'center', color: MUTED, border: `1px solid ${BORDER}`, borderRadius: 1.5 }}>No users found.</Box>}
    </Stack>

    <Dialog open={!!editing} onClose={() => !saving && setEditing(null)} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 900 }}>Office access</DialogTitle>
      <DialogContent>
        <Stack gap={1.15} sx={{ mt: 0.5 }}>
          <Box sx={{ p: 1.15, bgcolor: PANEL, borderRadius: 1.5 }}><Typography sx={{ fontWeight: 900 }}>{editing?.name || 'User'}</Typography><Typography sx={{ color: MUTED, fontSize: 12 }}>{editing?.email}</Typography></Box>
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: 13, color: DARK }}>Pages</Typography>
            {PAGE_OPTIONS.map((page) => <FormControlLabel key={page.key} control={<Checkbox checked={pages.includes(page.key)} onChange={() => toggle(page.key, pages, setPages)} />} label={page.label} />)}
          </Box>
          {PAGE_OPTIONS.filter((page) => pages.includes(page.key)).map((page) => (
            <Box key={page.key}>
              <Typography sx={{ fontWeight: 900, fontSize: 13, color: DARK }}>{page.label} actions</Typography>
              {page.actions.map((action) => <FormControlLabel key={action.key} control={<Checkbox checked={actions.includes(action.key)} onChange={() => toggle(action.key, actions, setActions)} />} label={action.label} />)}
            </Box>
          ))}
          {pages.length > 0 && <Box><Typography sx={{ fontWeight: 900, fontSize: 13, color: DARK, mb: 0.4 }}>Projects</Typography><Typography sx={{ color: MUTED, fontSize: 12, mb: 0.65 }}>Leave all unchecked to allow this user on every project.</Typography>{projects.map((project) => <FormControlLabel key={project.id} control={<Checkbox checked={projectIds.includes(project.id)} onChange={() => toggle(project.id, projectIds, setProjectIds)} />} label={project.name || project.id} />)}</Box>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}><Button onClick={() => setEditing(null)} disabled={saving}>Cancel</Button><Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save Office access'}</Button></DialogActions>
    </Dialog>

    <Dialog open={addOpen} onClose={closeAddDialog} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 900 }}>Add Office user</DialogTitle>
      <DialogContent>
        <Stack gap={1.15} sx={{ mt: 0.5 }}>
          {addError && <Alert severity="error">{addError}</Alert>}
          <Typography sx={{ color: MUTED, fontSize: 13 }}>This creates a login with only Office access. CRM page and action access will stay empty.</Typography>
          <TextField label="Full name" value={addName} onChange={(event) => setAddName(event.target.value)} fullWidth size="small" required />
          <TextField label="Email" value={addEmail} onChange={(event) => setAddEmail(event.target.value)} fullWidth size="small" required type="email" />
          <TextField label="Phone" value={addPhone} onChange={(event) => setAddPhone(event.target.value)} fullWidth size="small" required />
          <TextField label="Temporary password" value={addPassword} onChange={(event) => setAddPassword(event.target.value)} fullWidth size="small" required type="password" />
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: 13, color: DARK }}>Office pages</Typography>
            {PAGE_OPTIONS.map((page) => <FormControlLabel key={page.key} control={<Checkbox checked={addPages.includes(page.key)} onChange={() => toggle(page.key, addPages, setAddPages)} />} label={page.label} />)}
          </Box>
          {PAGE_OPTIONS.filter((page) => addPages.includes(page.key)).map((page) => (
            <Box key={page.key}>
              <Typography sx={{ fontWeight: 900, fontSize: 13, color: DARK }}>{page.label} actions</Typography>
              {page.actions.map((action) => <FormControlLabel key={action.key} control={<Checkbox checked={addActions.includes(action.key)} onChange={() => toggle(action.key, addActions, setAddActions)} />} label={action.label} />)}
            </Box>
          ))}
          {addPages.length > 0 && <Box><Typography sx={{ fontWeight: 900, fontSize: 13, color: DARK, mb: 0.4 }}>Projects</Typography><Typography sx={{ color: MUTED, fontSize: 12, mb: 0.65 }}>Leave all unchecked to allow this user on every project.</Typography>{projects.map((project) => <FormControlLabel key={project.id} control={<Checkbox checked={addProjectIds.includes(project.id)} onChange={() => toggle(project.id, addProjectIds, setAddProjectIds)} />} label={project.name || project.id} />)}</Box>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}><Button onClick={closeAddDialog} disabled={addSaving}>Cancel</Button><Button variant="contained" onClick={createOfficeUser} disabled={addSaving}>{addSaving ? 'Creating...' : 'Create Office user'}</Button></DialogActions>
    </Dialog>
  </Box>;
}
